import { useState } from "react";
import { ChevronRight, MoreVertical } from "lucide-react";
import {
  formatLanguages,
  languageListTitle,
  personaState,
  PersonaStatusIcon,
  useAvailableVariants,
  useSetPersona,
  type PersonaVariant,
} from "@/entities/persona";
import { AddPersonaButton } from "@/features/add-persona";
import type { ProcessingJobList } from "@/lib/api";
import { getProjectName } from "@/lib/projectName";
import { getStepLabel, getStepProgress } from "@/lib/processingSteps";

interface SidebarProjectItemProps {
  job: ProcessingJobList;
  isSelected: boolean;
  onSelect: (position: number) => void;
  onOpenMenu: (e: React.MouseEvent<HTMLButtonElement>, job: ProcessingJobList) => void;
}

const statusDot = (status: string) => {
  switch (status) {
    case "COMPLETED":
      return "bg-green-500";
    case "PROCESSING":
    case "PENDING":
      return "bg-blue-500 animate-pulse";
    case "FAILED":
      return "bg-red-500";
    default:
      return "bg-gray-400";
  }
};

/** One project row that expands into its persona list, Notion-style.
 *  Variants load only while expanded — collapsed rows cost no request. */
export function SidebarProjectItem({ job, isSelected, onSelect, onOpenMenu }: SidebarProjectItemProps) {
  const [expanded, setExpanded] = useState(false);
  const isProcessing = job.status === "PROCESSING" || job.status === "PENDING";
  const canExpand = job.status === "COMPLETED";

  const { data } = useAvailableVariants(expanded && canExpand ? String(job.id) : undefined);
  const setPersona = useSetPersona();

  const variants = data?.variants ?? [];
  const addable = data?.addable ?? [];

  // Same contract as ProjectView's handlePersonaChange: the variant's canonical role goes
  // with the switch so role + scenario follow the persona instead of staying on whatever
  // role was chosen last. Personas with no canonical role fall back to the bare persona.
  const openPersona = (variant: PersonaVariant) => {
    onSelect(job.position);
    if (variant.persona === job.persona) return;
    if (variant.role_slug) {
      setPersona.mutate({ jobId: job.id, roleSlug: variant.role_slug });
    } else {
      setPersona.mutate({ jobId: job.id, persona: variant.persona });
    }
  };

  return (
    <div className="relative">
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(job.position)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(job.position);
          }
        }}
        className={`relative w-full flex flex-col px-2 py-1.5 text-sm rounded-md transition-colors text-left cursor-pointer group ${
          isSelected ? "bg-muted font-medium" : "hover:bg-muted/50"
        }`}
      >
        <div className="flex items-center gap-1.5 pr-6">
          {canExpand ? (
            <button
              type="button"
              aria-label={expanded ? "Collapse project" : "Expand project"}
              aria-expanded={expanded}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              className="p-0.5 -ml-0.5 rounded hover:bg-muted-foreground/10"
            >
              <ChevronRight
                className={`w-3 h-3 text-muted-foreground transition-transform ${expanded ? "rotate-90" : ""}`}
              />
            </button>
          ) : (
            <span className="w-4 shrink-0" />
          )}
          <span className={`w-2 h-2 rounded-full shrink-0 ${statusDot(job.status)}`} />
          <span className="truncate flex-1 text-[13px]">{getProjectName(job)}</span>
        </div>

        {isProcessing && (
          <div className="pl-4 mt-0.5 space-y-1 pr-6">
            <span className="text-[12px] text-muted-foreground">{getStepLabel(job.step)}</span>
            <div className="h-[3px] w-full rounded-full bg-primary/20 overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500"
                style={{ width: `${getStepProgress(job.step)}%` }}
              />
            </div>
          </div>
        )}

        <button
          type="button"
          aria-label="Project options"
          onClick={(e) => onOpenMenu(e, job)}
          className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-muted-foreground/10 rounded p-1 transition-opacity"
        >
          <MoreVertical className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </div>

      {expanded && (
        <div className="ml-4 pl-2 border-l flex flex-col gap-px py-0.5">
          {variants.map((variant) => (
            <button
              key={variant.persona}
              type="button"
              onClick={() => openPersona(variant)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[12px] text-left transition-colors ${
                isSelected && variant.persona === job.persona
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:bg-muted/50"
              }`}
            >
              <PersonaStatusIcon state={personaState(variant)} />
              <span className="truncate flex-1">{variant.role_name}</span>
              <span
                className="text-[10px] uppercase tabular-nums opacity-70 shrink-0"
                title={languageListTitle(variant.languages, job.detected_language)}
              >
                {formatLanguages(variant.languages, job.detected_language)}
              </span>
            </button>
          ))}

          <AddPersonaButton
            jobId={job.id}
            addable={addable}
            variant="row"
            placement="right"
            onBeforeAdd={() => onSelect(job.position)}
          />
        </div>
      )}
    </div>
  );
}
